import React, { useEffect, useMemo, useRef } from "react";
import * as DTO from "../../../../../interfaces";
import "./styles.scss";
import { useAccountSettings, URLS } from "../../../../../utils";
import { WebComponents } from "../../../../../interfaces";
import { CategoryCard } from "../../../../CategoryCard";
import { AssetPreview } from "../../../../CategoryCard/components/AssetPreviewCard";
import { CampaignCategoryModal } from "../CampaignCategoryModal";
import { useHistory } from "react-router-dom";

interface OwnProps {
  campaignList: DTO.CampaignResume[];
  categories: DTO.Category[];
  coachId: string;
}

interface CategoryRow {
  type: "CATEGORIES" | "SELECTED_CATEGORY";
  categories: DTO.Category[];
}

type Props = OwnProps;

export const CategoryCards: React.FC<Props> = ({ campaignList, categories, coachId }) => {
  const otherCategory: DTO.Category = {
    name: "Uncategorized Campaigns",
    categoryId: "UNCATEGORIZED",
    type: "CAMPAIGN",
  };
  const draftCategory: DTO.Category = {
    name: "Unpublished Campaigns",
    categoryId: "UNPUBLISHED",
    type: "CAMPAIGN",
  };

  const [categoriesList, setCategoriesList] = React.useState<DTO.Category[]>([
    ...categories,
    otherCategory,
    draftCategory,
  ]);

  const [selectedCategory, setSelectedCategory] = React.useState<DTO.Category | undefined>(undefined);
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const history = useHistory();
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.PROMPTS);

  const uncategorizedCampaigns = useMemo(() => {
    return campaignList.filter(campaign => {
      return campaign.categoryIds?.length === 0 && campaign.campaignStatus !== "UNPUBLISHED";
    });
  }, [campaignList]);

  const unpublishedCampaigns = useMemo(() => {
    return campaignList.filter(campaign => {
      return campaign.campaignStatus === "UNPUBLISHED";
    });
  }, [campaignList]);

  const isPseudoCategory = (id?: string) => {
    return id === "UNPUBLISHED" || id === "UNCATEGORIZED";
  };

  useEffect(() => {
    setCategoriesList([...categories, otherCategory, draftCategory]);
  }, [categories]);

  const campaignsForCategory = (category: DTO.Category) => {
    if (category.categoryId == "UNPUBLISHED") return unpublishedCampaigns;
    else if (category.categoryId == "UNCATEGORIZED") return uncategorizedCampaigns;
    return campaignList.filter(campaign => {
      return campaign.categoryIds.includes(category.categoryId!);
    });
  };

  const toPreview = (c: DTO.CampaignResume) => {
    let thumbnail = undefined;
    if (c.firstPromptMedia?.category === "IMAGE") {
      thumbnail = c.firstPromptMedia.uri;
    } else if (c.firstPromptMedia?.thumbnailUri) {
      thumbnail = c.firstPromptMedia.thumbnailUri;
    }
    return {
      assetId: c.campaignId,
      thumbnail,
      href: URLS.coach.editCampaigns.replace(":campaignId", c.campaignId),
    } as AssetPreview;
  };

  const goToCampaign = (campaignId: string) => {
    history.push(URLS.coach.editCampaigns.replace(":campaignId", campaignId));
  };

  return (
    <div className="campaignCardsView">
      <div className={"cardsGrid"}>
        {categoriesList.map(category => {
          return (
            <CategoryCard
              assetList={campaignsForCategory(category).map(c => toPreview(c))}
              assetType={"Campaign"}
              category={category}
              selectCategory={c => {
                setSelectedCategory(c);
                setShowModal(true);
              }}
              selectAsset={a => goToCampaign(a.assetId)}
            />
          );
        })}
      </div>
      <CampaignCategoryModal
        open={showModal}
        onClose={() => setShowModal(false)}
        coachId={coachId}
        category={selectedCategory}
        campaigns={selectedCategory ? campaignsForCategory(selectedCategory) : []}
        canEdit={withWritePermission && !isPseudoCategory(selectedCategory?.categoryId)}
        onUpdate={newCategory => {
          setSelectedCategory(newCategory);
          setCategoriesList(l => l.map(c => (c.categoryId === newCategory.categoryId ? newCategory : c)));
        }}
      />
    </div>
  );
};
